import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TreeNode } from './TreeNode'
import { useDepartmentTree } from '@/hooks/useDepartments'
import type { ApiDepartment } from '@/types/department'

interface DepartmentTreeProps {
	selectedId: number | undefined
	onSelect: (id: number) => void
	onAddRoot: () => void
	onAddChild: (parentId: number) => void
	onEdit: (dept: ApiDepartment) => void
	onDelete: (dept: ApiDepartment) => void
}

export function DepartmentTree({
	selectedId,
	onSelect,
	onAddRoot,
	onAddChild,
	onEdit,
	onDelete,
}: DepartmentTreeProps) {
	const { data: treeData, isLoading, isError, error, refetch } = useDepartmentTree()

	return (
		<div className="flex flex-col h-full border-b lg:border-b-0 lg:border-r bg-muted/30">
			{/* 头部 */}
			<div className="p-4 border-b">
				<h2 className="font-semibold text-lg mb-3">部门结构</h2>
				<Button onClick={onAddRoot} className="w-full" size="sm">
					<Plus className="mr-1.5 h-4 w-4" />
					新增顶级部门
				</Button>
			</div>

			{/* 树内容 */}
			<div className="flex-1 overflow-auto py-2">
				{isLoading ? (
					<div className="px-4 space-y-2">
						{Array.from({ length: 4 }).map((_, i) => (
							<Skeleton key={i} className="h-8 w-full" />
						))}
					</div>
				) : isError ? (
					<div className="px-4 py-8 text-center">
						<p className="text-sm text-destructive mb-2">加载失败</p>
						<p className="text-xs text-muted-foreground mb-4">{(error as Error)?.message}</p>
						<Button variant="outline" size="sm" onClick={() => refetch()}>
							重试
						</Button>
					</div>
				) : !treeData || treeData.length === 0 ? (
					<div className="px-4 py-8 text-center">
						<p className="text-sm text-muted-foreground">暂无部门数据</p>
						<p className="text-xs text-muted-foreground mt-1">点击上方按钮创建部门</p>
					</div>
				) : (
					treeData.map((node) => (
						<TreeNode
							key={node.id}
							node={node}
							selectedId={selectedId}
							onSelect={onSelect}
							onAddChild={onAddChild}
							onEdit={onEdit}
							onDelete={onDelete}
						/>
					))
				)}
			</div>
		</div>
	)
}
